/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Voice Studio Audio Gallery DTOs (Task 7.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Functional origin of an audio asset in the workspace library.
 */
export const AudioSourceTypeSchema = z.enum(['tts', 'clone', 'mix', 'stt']).openapi({
  description: 'Audio origin: tts (synthesized speech), clone (voice samples/profiles), mix (rendered mixes), stt (transcribed sources)',
  example: 'tts',
});

export type AudioSourceType = z.infer<typeof AudioSourceTypeSchema>;

/**
 * Compact audio library item.
 */
export const AudioItemSchema = z
  .object({
    id: z.string().openapi({ description: 'Audio asset ID', example: 'b0d6f0c4-0a3e-4d0a-9d4a-1d5f7c1a9a11' }),
    workspaceId: z.string().openapi({ description: 'Owning workspace ID', example: 'ws_01' }),
    sourceType: AudioSourceTypeSchema,
    title: z.string().openapi({ description: 'Display title (custom title, or derived from the source prompt)', example: 'Welcome narration' }),
    audioUrl: z.string().openapi({ description: 'Public zero-egress CDN playback URL', example: 'https://media.sophiaplatform.com/audio/ws_01/tts/a.mp3' }),
    mimeType: z.string().openapi({ example: 'audio/mpeg' }),
    format: z.string().openapi({ description: 'Container format derived from the storage key', example: 'mp3' }),
    sizeBytes: z.number().int().nonnegative().openapi({ example: 48211 }),
    durationSeconds: z.number().nullable().openapi({ description: 'Duration in seconds when known', example: 6.4 }),
    provider: z.string().nullable().openapi({ example: 'openai' }),
    model: z.string().nullable().openapi({ example: 'tts-1' }),
    voiceId: z.string().nullable().openapi({ example: 'alloy' }),
    tags: z.array(z.string()).openapi({ example: ['intro', 'podcast'] }),
    isFavorite: z.boolean().openapi({ example: false }),
    creditsCost: z.number().openapi({ example: 0.0161 }),
    createdAt: z.string().openapi({ description: 'ISO 8601 creation timestamp', example: '2026-10-22T03:00:00.000Z' }),
  })
  .openapi('AudioItem');

export type AudioItemDto = z.infer<typeof AudioItemSchema>;

/**
 * Query parameters for listing audio assets.
 */
export const ListAudioQuerySchema = z
  .object({
    sourceType: AudioSourceTypeSchema.optional(),
    provider: z.string().min(1).optional().openapi({ description: 'Filter by provider (openai, elevenlabs, google, azure, ...)', example: 'openai' }),
    voiceId: z.string().min(1).optional().openapi({ description: 'Filter by voice identifier', example: 'alloy' }),
    tag: z.string().min(1).optional().openapi({ description: 'Filter by a single tag', example: 'intro' }),
    isFavorite: z
      .enum(['true', 'false'])
      .optional()
      .openapi({ description: 'Filter favorites only (true) or non-favorites (false)', example: 'true' }),
    createdFrom: z.string().datetime().optional().openapi({ description: 'Inclusive lower bound of creation time (ISO 8601)' }),
    createdTo: z.string().datetime().optional().openapi({ description: 'Inclusive upper bound of creation time (ISO 8601)' }),
    sortBy: z.enum(['createdAt', 'duration', 'size']).default('createdAt').openapi({ example: 'createdAt' }),
    sortOrder: z.enum(['asc', 'desc']).default('desc').openapi({ example: 'desc' }),
    page: z.coerce.number().int().min(1).default(1).openapi({ example: 1 }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({ example: 20 }),
    cursor: z.string().optional().openapi({ description: 'Opaque cursor returned as nextCursor; takes precedence over page' }),
  })
  .openapi('ListAudioQuery');

export type ListAudioQueryDto = z.infer<typeof ListAudioQuerySchema>;

export const ListAudioResponseSchema = z
  .object({
    items: z.array(AudioItemSchema),
    pagination: z.object({
      page: z.number().int(),
      limit: z.number().int(),
      total: z.number().int(),
      totalPages: z.number().int(),
    }),
    nextCursor: z.string().nullable().openapi({ description: 'Cursor for the next page, null when exhausted' }),
  })
  .openapi('ListAudioResponse');

export type ListAudioResponseDto = z.infer<typeof ListAudioResponseSchema>;

/**
 * Detailed audio asset view with technical parameters and full metadata.
 */
export const AudioDetailResponseSchema = AudioItemSchema.extend({
  r2Key: z.string().openapi({ example: 'audio/ws_01/tts/2026/10/a.mp3' }),
  prompt: z.string().nullable().openapi({ description: 'Original source text' }),
  note: z.string().nullable().openapi({ description: 'Free-form note' }),
  characterCount: z.number().int().nullable(),
  bitrateKbps: z.number().nullable().openapi({ description: 'Approximate bitrate computed from size and duration' }),
  transactionType: z.string().nullable().openapi({ description: 'Billing transaction type recorded at creation', example: 'tts_generation' }),
  metadata: z.record(z.string(), z.unknown()).openapi({ description: 'Raw asset metadata' }),
}).openapi('AudioDetailResponse');

export type AudioDetailResponseDto = z.infer<typeof AudioDetailResponseSchema>;

/**
 * Partial update of user-editable audio metadata.
 */
export const UpdateAudioMetadataSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional().openapi({ example: 'Welcome narration v2' }),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional().openapi({ example: ['intro', 'final'] }),
    note: z.string().max(2000).nullable().optional().openapi({ example: 'Approved by marketing' }),
    isFavorite: z.boolean().optional().openapi({ example: true }),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' })
  .openapi('UpdateAudioMetadata');

export type UpdateAudioMetadataDto = z.infer<typeof UpdateAudioMetadataSchema>;

export const DeleteAudioResponseSchema = z
  .object({
    success: z.literal(true),
    audioId: z.string(),
    r2ObjectDeleted: z.boolean().openapi({ description: 'Whether the physical R2 object was removed' }),
  })
  .openapi('DeleteAudioResponse');

export type DeleteAudioResponseDto = z.infer<typeof DeleteAudioResponseSchema>;

export const AudioBatchDeleteSchema = z
  .object({
    audioIds: z.array(z.string().min(1)).min(1).max(100).openapi({ description: 'Audio asset IDs to delete (max 100)' }),
  })
  .openapi('AudioBatchDelete');

export type AudioBatchDeleteDto = z.infer<typeof AudioBatchDeleteSchema>;

export const AudioBatchDeleteResponseSchema = z
  .object({
    success: z.literal(true),
    deletedIds: z.array(z.string()),
    notFoundIds: z.array(z.string()).openapi({ description: 'IDs that do not exist in this workspace' }),
    totalDeleted: z.number().int(),
  })
  .openapi('AudioBatchDeleteResponse');

export type AudioBatchDeleteResponseDto = z.infer<typeof AudioBatchDeleteResponseSchema>;
